import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import classNames from 'classnames'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {ImgButton, SimpleBlueButton} from '../../components/core/button'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import CustomSelect from '../../components/core/customSelect'
import Dropdown from '../../components/core/dropdown'
import Tooltip from '../../components/core/tooltip'
import IntervalSettings from '../../components/pages/rulesetIntervalForm'
import plans from '../../data/plans.json'
import {
    EditIcon,
    FilterIcon,
    HorizontalMoreIcon,
    InstructionIcon,
    PausedIcon,
    PlayIcon,
    PlusIcon,
    TrashIcon
} from '../../icons'
import {routes} from '../../routes'
import {setConfirmIsActive} from '../../stores/alert'
import {getAllLimitUsages} from '../../stores/auth'
import {deleteServerResources, getServerResources} from '../../stores/resources'
import {getMetricBasedRulesets, getServerResource, updateServerResource} from '../../stores/server-monitoring'
import {colors} from '../../utils/colors'
import limitUsageManager from '../../utils/hooks/limitUsageManager'
import renderSkeletonLoaders from '../../utils/loader'
import NavigationHelper from '../../utils/navigation'
import TextHelper from '../../utils/text'
import {getIntervalUnitAndValue} from '../../utils/intervalManagerService'
import urgencyOptions from '../../data/selectOptions/urgency.json'
import Input from '../../components/core/input/index.jsx'
import Textarea from '../../components/core/textarea/index.jsx'
import Checkbox from '../../components/core/checkbox/index.jsx'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations.js'
import {envConfig} from '../../utils/env.js'
import {buildMetricOptions, getRelevantMetricKeys} from '../../utils/rulesetMetrics.js'
import {isNewFeatureEnabled} from '../../utils/newFeature'

const {isReadOnlyModeEnabled} = envConfig

const getIntervalSelectOptions = intervalUnits => Object.values(intervalUnits)?.filter(unitInfo => unitInfo.value !== intervalUnits.second.value)
const DEFAULT_INTERVAL_VALUE = 5

const ActionButton = ({
    onClick,
    isDarkMode,
    isButtonLoader,
    disabled,
    IconComponent,
    buttonText,
    tooltipContent = null,
    className = ''
}) => {
    const button = (
        <ImgButton
            className={classNames('light-bg', className, {
                dark: isDarkMode
            })}
            disabled={disabled}
            onClick={onClick}
        >
            <span>{isButtonLoader && !disabled ? <ButtonLoader /> : <IconComponent />}</span>
            {buttonText && <span className="button-text">{buttonText}</span>}
        </ImgButton>
    )

    return tooltipContent ? (
        <Tooltip placement="top" trigger="mouseenter" content={tooltipContent}>
            {button}
        </Tooltip>
    ) : (
        button
    )
}

const filterMetricsByGroup = (data, groupedMetricTypes = {}, usageLimits, isServerEnabled = true) => {
    const categories = Object.keys(data)
    const result = {}
    categories.map(category => {
        const {usage} = usageLimits[category]
        result[category] = {
            ...data?.[category],
            child_items: data?.[category]?.child_items.filter(item =>
                groupedMetricTypes?.[category]?.includes(item.value)
            ),
            usage: !isServerEnabled ? (groupedMetricTypes?.[category]?.length || 0) + usage : usage
        }
    })
    return result
}

const getConditionKeyOptions = (metric, modalId) => {
    if (!metric?.charts) return []

    if (modalId) {
        return Object.values(metric.charts[modalId]?.condition?.options ?? {})
    }

    return Object.values(metric.charts).reduce((options, chart) => {
        if (chart?.condition?.options) {
            options.push(...Object.values(chart.condition.options))
        }
        return options
    }, [])
}

const getRulesetFormInitialValues = (activeModal, rulesets, conditionTypes, rulesetMetricKeys, serverMetrics, serverResource) => {
    const existingRuleset = activeModal?.ruleset
    const isExistingServerResource = !!serverResource?.id
    const metricOptions = buildMetricOptions(rulesetMetricKeys, serverMetrics)
        .map(option => ({
            ...option,
            isDisabled: !isExistingServerResource && !!option.is_setup_required
        }))
        .sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled))

    const relevantMetricKeys = activeModal?.modal_id
        ? getRelevantMetricKeys(activeModal.metric_type, activeModal.modal_id)
        : null
    const relevantTypeOptions = relevantMetricKeys
        ? metricOptions.filter(option => relevantMetricKeys.includes(option.value))
        : metricOptions

    const typeOptions = relevantTypeOptions.length ? relevantTypeOptions : metricOptions

    let conditionKeyOptions

    if (activeModal?.modal_id) {
        const activeServerMetric = Object.values(serverMetrics ?? {}).find(metric => metric.value === activeModal.metric_type)
        conditionKeyOptions = getConditionKeyOptions(activeServerMetric, activeModal.modal_id)
    } else {
        conditionKeyOptions = []
    }

    const typeFieldInfoText = isExistingServerResource
        ? null
        : 'Per-component metrics are disabled here. Save this resource first, then edit it to target a specific component.'

    const lastTimeField = (existingValue) => ({
        name: 'last_time',
        placeholder: 'e.g. 1',
        label: 'For the last (min)',
        type: 'number',
        form_type: 'input',
        FormItem: Input,
        info_text: 'Specify the time range for which the condition should be evaluated.',
        value: existingValue ?? 1
    })

    return {
        ...rulesets.items,
        ...Object.fromEntries(
            Object.entries(rulesets.items).map(([key, item]) => [
                key,
                {
                    ...item,
                    FormItem:
                        item?.form_type === 'select' ? CustomSelect :
                            item?.form_type === 'checkbox' ? Checkbox :
                                item?.form_type === 'textarea' ? Textarea :
                                    Input,
                    ...(item?.name === 'type' && typeFieldInfoText && {
                        below_text: typeFieldInfoText
                    }),
                    ...(item?.form_type === 'select' && {
                        options:
                            item?.name === 'type'
                                ? typeOptions
                                : item?.name === 'urgency'
                                    ? Object.values(urgencyOptions)
                                    : item?.name === 'value_type'
                                        ? item.options
                                        : [],
                        value:
                            item?.name === 'type'
                                ? (existingRuleset?.type ?? '')
                                : item?.name === 'urgency'
                                    ? (existingRuleset?.urgency?.value ?? urgencyOptions.low)
                                    : item?.name === 'value_type'
                                        ? (existingRuleset?.value_type?.value ?? item.value)
                                        : ''
                    }),
                    ...(existingRuleset && item?.form_type !== 'select' && {
                        value: existingRuleset?.[key]?.value ?? item?.value
                    }),
                    ...(existingRuleset && item?.form_type === 'checkbox' && {
                        value: existingRuleset?.[key]?.value ?? item?.value
                    })
                }
            ])
        ),
        conditions: {
            ...rulesets.items.conditions,
            pairs: existingRuleset?.conditions?.pairs?.length
                ? existingRuleset.conditions.pairs.map(pair => ({
                    values: {
                        ...rulesets.items.conditions.pairs[0].values,
                        FormItem: Input,
                        value: pair.values?.value ?? ''
                    },
                    operator: {
                        ...rulesets.items.conditions.pairs[0].operator,
                        FormItem: CustomSelect,
                        options: Object.values(conditionTypes.json_key_value_check.values).filter(
                            conditionType =>
                                conditionType?.value !== 'contains' &&
                                conditionType?.value !== 'not-contains'
                        ),
                        value: pair.operator?.value ?? conditionTypes.json_key_value_check.values.less_than
                    },
                    last_time: {
                        ...rulesets.items.conditions.pairs[0].last_time,
                        FormItem: Input,
                        value: pair.last_time?.value ?? 1
                    }
                }))
                : rulesets.items.conditions.pairs.map(condition => ({
                    values: {
                        ...condition.values,
                        FormItem: Input
                    },
                    operator: {
                        ...condition.operator,
                        FormItem: CustomSelect,
                        options: Object.values(conditionTypes.json_key_value_check.values).filter(
                            conditionType =>
                                conditionType?.value !== 'contains' &&
                                conditionType?.value !== 'not-contains'
                        ),
                        value: conditionTypes.json_key_value_check.values.less_than
                    },
                    last_time: {
                        ...rulesets.items.conditions.pairs[0].last_time,
                        FormItem: Input,
                        value: condition.last_time?.value ?? 1
                    }
                }))
        }
    }
}

const findMetricByValue = (category, metricValue) => {
    return category?.child_items?.find(item => item.value === metricValue)
}

const getServerResourceMetrics = sr => {
    return !sr ?
        null :
        {
            metric_types_basic: sr?.grouped_metric_types?.basic,
            metric_types_advanced: sr?.grouped_metric_types?.advanced,
            metric_types_complex: sr?.grouped_metric_types?.complex
        }
}

const isStatusDisabled = (selectedMetricTypes, resource, usageLimits) => {
    return (selectedMetricTypes?.basic?.usage > usageLimits?.basic?.value && resource?.grouped_metric_types?.basic?.length > 0)
        || (selectedMetricTypes?.advanced?.usage > usageLimits?.advanced?.value && resource?.grouped_metric_types?.advanced?.length > 0)
        || (selectedMetricTypes?.complex?.usage > usageLimits?.complex?.value && resource?.grouped_metric_types?.complex?.length > 0)
}

const getTooltipText = (resource, selectedMetricTypes, isIntervalLimitOverExceeded, usageLimits, text) => {
    if (usageLimits?.serverResource?.isLimitExceeded) {
        return TextHelper.replaceAllChars(text.status_disabled_tooltip_text_server, ':limit_title',
            usageLimits.serverResource?.title?.toLowerCase())
    }
    if (isStatusDisabled(selectedMetricTypes, resource, usageLimits)) {
        return TextHelper.replaceAllChars(text.status_disabled_tooltip_text_feature, ':limit_title', 'metric types')
    }
    if (isIntervalLimitOverExceeded && resource?.settings?.interval) {
        return TextHelper.replaceAllChars(text.status_disabled_tooltip_text_feature, ':limit_title', 'incident ruleset')
    }
    return null
}

const useHeaderActions = ({isFetching = false, selectedResources, serverResource, showStatusBtns = true}) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const dropdownMenuRef = useRef()
    const conditionTypes = useNamespaceTranslations('condition-types')?.resource_conditions
    const text = useNamespaceTranslations('server-monitoring')
    const intervalUnits = useNamespaceTranslations('interval-units')?.interval_units
    const serverMetrics = useNamespaceTranslations('server-metrics')?.server_metrics
    const usageLimitsData = useNamespaceTranslations('usage-limits')
    const [state, setState] = useState({
        deletedResourceId: null,
        isButtonLoader: false,
        isButtonDisabled: true,
        intervalValues: {
            interval_value: DEFAULT_INTERVAL_VALUE,
            interval_unit: intervalUnits.minute
        },
        manualUpdate: true,
        activeIncidentsModalId: '',
        isAdvancedEnabled: false
    })
    const [selectedMetricTypes, setSelectedMetricTypes] = useState({})
    const [filteredMetrics, setFilteredMetrics] = useState([])
    const [selectedServerResource, setSelectedServerResource] = useState({})
    const [activeModal, setActiveModal] = useState({type: null, metric_type: null, modal_id: null})
    const serverResources = useSelector(state => state.resources.serverResources)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const organisationLimitUsages = useSelector((state) => state.auth.organisationLimitUsages)
    const rulesetMetricKeys = useSelector(state => state.server_monitoring.rulesetMetricKeys)
    const isFreePlan = localStorage.getItem('plan') === plans.free_v2.key
    const selectedResource = serverResources.find(sr => sr?.id === selectedResources?.[0])

    const rulesetFormInitialValues = useMemo(
        () => getRulesetFormInitialValues(activeModal, text.steps.rulesets, conditionTypes, rulesetMetricKeys, serverMetrics, serverResource),
        [activeModal, rulesetMetricKeys, serverResource?.id]
    )

    const fetchRulesets = useCallback(() => {
        if (!isNewFeatureEnabled() || !serverResource?.id) return

        dispatch(getMetricBasedRulesets(serverResource.id, {
            serverMetrics,
            conditionTypeOptions: Object.values(conditionTypes?.json_key_value_check?.values ?? {}),
            valueTypeOptions: text.steps.rulesets.items.value_type.options
        }))
    }, [dispatch, serverResource?.id, serverMetrics, conditionTypes, text.steps.rulesets.items.value_type.options])

    const usageLimits = {
        serverResource: limitUsageManager(usageLimitsData.limit_modules.monitoring.module,
            usageLimitsData.limit_keys.server_resource_count.limit_key, organisationLimitUsages),
        basic: limitUsageManager(usageLimitsData.limit_modules.monitoring.module,
            usageLimitsData.limit_keys.server_basic_resource_count.limit_key, organisationLimitUsages),
        advanced: limitUsageManager(usageLimitsData.limit_modules.monitoring.module,
            usageLimitsData.limit_keys.server_advanced_resource_count.limit_key, organisationLimitUsages),
        complex: limitUsageManager(usageLimitsData.limit_modules.monitoring.module,
            usageLimitsData.limit_keys.server_complex_resource_count.limit_key, organisationLimitUsages),
        incident_ruleset: limitUsageManager(usageLimitsData.limit_modules.incident_management.module,
            usageLimitsData.limit_keys.server_monitoring_incident_ruleset_count.limit_key, organisationLimitUsages)
    }

    const {
        usage: intervalUsage,
        value: intervalLimit
    } = usageLimits.incident_ruleset

    const [interactiveIntervalUsage, setInteractiveIntervalUsage] = useState(intervalUsage)
    const isIntervalLimitExceeded = interactiveIntervalUsage >= intervalLimit
    const isIntervalLimitOverExceeded = interactiveIntervalUsage > intervalLimit

    const handleIncidentMenu = (id) => {
        setState(prev => ({...prev, activeIncidentsModalId: id === state.activeIncidentsModalId ? '' : id}))
    }

    const handleAdvancedSettings = (isManualUpdate = false, isBtnDisabled = false) => {
        const updatedIntervalUsage = !state.isAdvancedEnabled ? interactiveIntervalUsage + 1 : interactiveIntervalUsage - 1
        setState(prev => ({...prev, manualUpdate: isManualUpdate, isAdvancedEnabled: !state.isAdvancedEnabled && !isIntervalLimitExceeded}))
        setInteractiveIntervalUsage(updatedIntervalUsage)
        isBtnDisabled && setState(prev => ({...prev, isButtonDisabled: updatedIntervalUsage > intervalLimit && !state.isAdvancedEnabled}))
    }

    const handleChangeIntervalValues = (name, value, isCheckbox, isUpdateManual = false) => {
        const updatedValues = {...state.intervalValues}
        updatedValues[name] = !isCheckbox ? value.target.value : value
        setState(prev => ({
            ...prev,
            manualUpdate: isUpdateManual,
            isButtonDisabled: isIntervalLimitExceeded,
            intervalValues: updatedValues
        }))
    }

    const isIntervalSettingDisabled = isFreePlan || (isIntervalLimitExceeded && !state.isAdvancedEnabled)

    const renderIntervalSettings = useCallback((isUpdateManual = false) => (
        <IntervalSettings
            isAdvancedEnabled={state.isAdvancedEnabled}
            onChangeAdvancedSettings={() => handleAdvancedSettings(isUpdateManual, true)}
            onChangeFormValues={(name, value, isCheckbox) => handleChangeIntervalValues(name, value, isCheckbox, isUpdateManual)}
            isIntervalSettingDisabled={isIntervalSettingDisabled}
            values={state.intervalValues}
            intervalLimit={intervalLimit}
            intervalUsage={interactiveIntervalUsage}
            intervalSelectOptions={getIntervalSelectOptions(intervalUnits)}
            intervalLabelText={text.interval_based_text}
            intervalInfoText={text.interval_info_text}
            menuPosition="fixed"
            isNewInfoIcon
        />
    ), [handleChangeIntervalValues,
        state.intervalValues,
        intervalLimit,
        intervalUsage,
        state.isAdvancedEnabled,
        handleAdvancedSettings])

    const handleEdit = (updatedResourceId, fromPath) => {
        navigate(NavigationHelper.getNavigationUrl(
            NavigationHelper.getLocalizedPath(routes.update_server_monitoring.paths),
            ':id',
            updatedResourceId),
        {state: {from: fromPath}})
        dispatch(getAllLimitUsages())
    }

    const handleSelectedMetricTypes = (categoryKey, metricValue, isMetricDisabled = false, isUpdateManual = false) => {
        setState(prev => ({...prev, isButtonDisabled: false, manualUpdate: isUpdateManual}))
        const updatedMetrics = {...selectedMetricTypes}
        const category = updatedMetrics[categoryKey]
        const childItems = category?.child_items || []

        const isMetricSelected = childItems.some((item) => item.value === metricValue)
        const updatedChildItems = isMetricSelected
            ? childItems.filter((item) => item.value !== metricValue)
            : isMetricDisabled
                ? childItems
                : [...childItems, {...findMetricByValue(text.steps.metrics.items[categoryKey], metricValue)}]

        updatedMetrics[categoryKey] = {
            ...category,
            child_items: updatedChildItems,
            usage: isMetricSelected ? category?.usage - 1
                : isMetricDisabled
                    ? category?.usage
                    : category?.usage + 1
        }
        setSelectedMetricTypes(updatedMetrics)
    }

    const openDeleteResourceConfirm = (id) => {
        dispatch(setConfirmIsActive(true))
        if (id?.length === 1) {
            setState(prev => ({...prev, deletedResourceId: id}))
        }
    }

    const deleteResource = async (deletedId) => {
        await dispatch(deleteServerResources(deletedId))
        setState(prev => ({...prev, deletedResourceId: null}))
        navigate(NavigationHelper.getLocalizedPath(routes.server_monitoring.paths))
        await dispatch(getAllLimitUsages())
    }

    const handleStatus = async (serverResource) => {
        setState(prev => ({...prev, isButtonLoader: true}))
        await dispatch(updateServerResource({...serverResource, interval: serverResource?.settings?.interval}, false, serverResource?.id))
        setState(prev => ({...prev, isButtonLoader: false}))
        showStatusBtns ?
            await dispatch(getServerResources()) :
            await dispatch(getServerResource(serverMetrics, false, serverResource?.id, navigate, true, true))
        await dispatch(getAllLimitUsages())
        setSelectedServerResource(serverResource)
    }

    const handleAddServer = (navigate, fromPath) => {
        navigate(NavigationHelper.getLocalizedPath(routes.create_server_monitoring.paths), {state: {from: fromPath}})
        dispatch(getAllLimitUsages())
    }

    const getActions = useCallback(({
        state,
        toggleInstructionModal,
        activeMetrics,
        setActiveMetrics,
        serverGroupedMetricValues,
        serverMetricValues,
        onFilter}) => {
        const isSingleResourceSelected = selectedResources?.length === 1
        const isEnableBtnDisabled = isFreePlan || isStatusDisabled(selectedMetricTypes, selectedServerResource, usageLimits) ||
            usageLimits.serverResource.isLimitExceeded || (isIntervalLimitOverExceeded && selectedServerResource?.settings?.interval)

        return (
            <div className="selected-actions">
                {isFetching ? (
                    renderSkeletonLoaders(showStatusBtns ? 4 : 3, 'btn-skeleton')
                ) : (
                    <>
                        {activeMetrics &&
                            <Dropdown
                                align='start'
                                closeMenu={true}
                                innerRef={dropdownMenuRef}
                                menuButton={<button className="filter-button"><FilterIcon />{text.filters}</button>}>
                                <CustomSelect
                                    isMulti
                                    placeholder={text.select_metrics}
                                    options={serverMetricValues}
                                    value={activeMetrics}
                                    isOptionDisabled={option => !serverGroupedMetricValues?.includes(option?.value)}
                                    name="metrics"
                                    infoText={text.select_metrics_info}
                                    onChange={(_, value) => {
                                        setActiveMetrics(value)
                                    }} />
                                <div className="d-flex w-100 flex-column gap-1">
                                    <SimpleBlueButton
                                        className="outline w-100"
                                        onClick={() => onFilter({isReset: true})}>
                                        {text.reset}
                                    </SimpleBlueButton>
                                    <SimpleBlueButton
                                        className="w-100"
                                        onClick={() => onFilter({isReset: false})}>
                                        {text.apply}
                                    </SimpleBlueButton>
                                </div>
                            </Dropdown>}
                        <ActionButton
                            onClick={() => handleAddServer(navigate, state)}
                            isDarkMode={isDarkMode}
                            isButtonLoader={false}
                            disabled={false}
                            IconComponent={() => (
                                <PlusIcon stroke={colors.colorMuted} />
                            )}
                            buttonText=""
                            className="responsive-add-button"
                        />
                        {toggleInstructionModal && <ActionButton
                            onClick={toggleInstructionModal}
                            isDarkMode={isDarkMode}
                            isButtonLoader={false}
                            disabled={false}
                            IconComponent={() => (
                                <InstructionIcon stroke={colors.colorMuted} />
                            )}
                            buttonText={text.instructions}
                        />}
                        {showStatusBtns ? <>
                            <ActionButton
                                onClick={() =>
                                    handleStatus({
                                        ...selectedServerResource,
                                        enabled: false
                                    })
                                }
                                isDarkMode={isDarkMode}
                                isButtonLoader={state.isButtonLoader}
                                disabled={!isSingleResourceSelected || !selectedServerResource?.enabled || isReadOnlyModeEnabled}
                                IconComponent={PausedIcon}
                                buttonText={text.disable}
                            />
                            <ActionButton
                                onClick={() =>
                                    handleStatus({
                                        ...selectedServerResource,
                                        enabled: true
                                    })
                                }
                                isDarkMode={isDarkMode}
                                isButtonLoader={state.isButtonLoader}
                                disabled={selectedServerResource?.enabled || isEnableBtnDisabled || !isSingleResourceSelected || isReadOnlyModeEnabled}
                                IconComponent={PlayIcon}
                                buttonText={text.enable}
                                tooltipContent={!selectedServerResource?.enabled && isSingleResourceSelected &&
                                    isEnableBtnDisabled && getTooltipText(selectedServerResource,
                                    selectedMetricTypes, isIntervalLimitOverExceeded, usageLimits, text)}
                            />
                        </> : selectedServerResource?.enabled ?
                            <ActionButton
                                onClick={() =>
                                    handleStatus({
                                        ...selectedServerResource,
                                        enabled: false
                                    })
                                }
                                isDarkMode={isDarkMode}
                                isButtonLoader={state.isButtonLoader}
                                disabled={!selectedServerResource?.enabled || isReadOnlyModeEnabled}
                                IconComponent={PausedIcon}
                                buttonText={text.disable}
                            /> :
                            <ActionButton
                                onClick={() =>
                                    handleStatus({
                                        ...selectedServerResource,
                                        enabled: true
                                    })
                                }
                                isDarkMode={isDarkMode}
                                isButtonLoader={state.isButtonLoader}
                                disabled={isEnableBtnDisabled || isReadOnlyModeEnabled}
                                IconComponent={PlayIcon}
                                buttonText={text.enable}
                                tooltipContent={getTooltipText(selectedServerResource,
                                    selectedMetricTypes, isIntervalLimitOverExceeded, usageLimits, text)}
                            />}
                        <ActionButton
                            onClick={() =>
                                handleEdit(selectedServerResource?.id, state)
                            }
                            isDarkMode={isDarkMode}
                            isButtonLoader={false}
                            disabled={isFreePlan || (showStatusBtns && !selectedResources?.length) || isReadOnlyModeEnabled}
                            IconComponent={EditIcon}
                            buttonText={text.edit}
                        />
                        <ActionButton
                            onClick={() =>
                                openDeleteResourceConfirm(selectedResources)
                            }
                            isDarkMode={isDarkMode}
                            isButtonLoader={false}
                            disabled={showStatusBtns && !selectedResources?.length || isReadOnlyModeEnabled}
                            IconComponent={() => (
                                <TrashIcon stroke={colors.colorRed} />
                            )}
                            buttonText={text.delete}
                            className="outline-red"
                        />
                    </>
                )}
            </div>
        )
    }, [handleAddServer,
        handleEdit,
        selectedServerResource,
        handleStatus,
        openDeleteResourceConfirm,
        isFetching,
        state.isButtonLoader,
        isDarkMode,
        text,
        usageLimits.serverResource])

    const getActionsMenu = (serverResource, state) => {
        const modifiedServerResource = {...serverResource, ...getServerResourceMetrics(serverResource)}
        const isEnableBtnDisabled = !modifiedServerResource?.enabled &&
            (isStatusDisabled(selectedMetricTypes, modifiedServerResource, usageLimits) ||
                usageLimits.serverResource.isLimitExceeded || (isIntervalLimitOverExceeded && modifiedServerResource?.settings?.interval))

        return <Dropdown
            menuButton={<button className={classNames({
                'table-action-btn': true,
                dark: isDarkMode
            })}><HorizontalMoreIcon /></button>}
            onMenuChange={() => setSelectedServerResource(modifiedServerResource)}>
            <MenuItem
                onClick={() => handleEdit(modifiedServerResource?.id, state)}
                disabled={isFreePlan || isReadOnlyModeEnabled}>
                <span className="d-flex"><EditIcon /></span>
                <span>{text.edit}</span>
            </MenuItem>
            {isEnableBtnDisabled ?
                <Tooltip
                    placement='top'
                    trigger='mouseenter'
                    content={getTooltipText(modifiedServerResource, selectedMetricTypes, isIntervalLimitOverExceeded, usageLimits, text)} >
                    <MenuItem
                        onClick={() => handleStatus({...modifiedServerResource, enabled: !modifiedServerResource?.enabled})}
                        disabled={true}>
                        <span className="d-flex">{modifiedServerResource?.enabled ? <PausedIcon /> : <PlayIcon />}</span>
                        <span>{text.enable}</span>
                    </MenuItem>
                </Tooltip> :
                <MenuItem
                    onClick={() => handleStatus({...modifiedServerResource, enabled: !modifiedServerResource?.enabled})}
                    disabled={isFreePlan || isReadOnlyModeEnabled}>
                    <span className="d-flex">{modifiedServerResource?.enabled ? <PausedIcon /> : <PlayIcon />}</span>
                    <span>{modifiedServerResource?.enabled ? text.disable : text.enable}</span>
                </MenuItem>}
            <MenuDivider />
            <MenuItem
                className="danger" onClick={() => openDeleteResourceConfirm([modifiedServerResource?.id])} disabled={isReadOnlyModeEnabled}>
                <span className="d-flex"><TrashIcon stroke={colors.colorRed} /></span>
                <span>{text.delete}</span>
            </MenuItem>
        </Dropdown>
    }

    useEffect(() => {
        dispatch(getAllLimitUsages())
    }, [])

    useEffect(() => {
        if (!state.manualUpdate) {
            return
        }
        setSelectedMetricTypes(
            selectedServerResource?.id ?
                filterMetricsByGroup(text.steps.metrics.items, selectedServerResource?.grouped_metric_types,
                    usageLimits, selectedServerResource?.enabled) :
                {
                    basic: {
                        ...text.steps.metrics.items.basic,
                        usage: usageLimits.basic.usage + 2
                    },
                    advanced: {
                        ...text.steps.metrics.items.advanced,
                        usage: usageLimits.advanced.usage,
                        child_items: []
                    },
                    complex: {
                        ...text.steps.metrics.items.complex,
                        usage: usageLimits.complex.usage,
                        child_items: []
                    }
                }
        )
    }, [usageLimits?.basic?.usage,
        usageLimits?.advanced?.usage,
        usageLimits?.complex?.usage,
        selectedServerResource?.grouped_metric_types?.basic,
        selectedServerResource?.grouped_metric_types?.advanced,
        selectedServerResource?.grouped_metric_types?.complex])

    useEffect(() => {
        setSelectedServerResource(serverResource?.id ?
            ({...serverResource, ...getServerResourceMetrics(serverResource)}) :
            ({...selectedResource, ...getServerResourceMetrics(selectedResource)}))
    }, [serverResource, selectedResource])

    useEffect(() => {
        if (!state.manualUpdate) {
            return
        }
        const updatedIntervalValues = getIntervalUnitAndValue(selectedServerResource?.settings?.interval, intervalUnits)
        setState(prev => ({
            ...prev,
            intervalValues: {
                interval_value: selectedServerResource?.settings?.interval ? updatedIntervalValues?.unitValue : DEFAULT_INTERVAL_VALUE,
                interval_unit: selectedServerResource?.settings?.interval ? intervalUnits?.[updatedIntervalValues?.unit] : intervalUnits.minute
            },
            isAdvancedEnabled: !!selectedServerResource?.settings?.interval
        }))
        setInteractiveIntervalUsage(
            !!selectedServerResource?.settings?.interval && !selectedServerResource?.enabled ?
                intervalUsage + 1 : intervalUsage)
    }, [selectedServerResource, intervalUsage])

    return {
        handleAddServer,
        getActions,
        getActionsMenu,
        deleteResource,
        state,
        setState,
        activeModal,
        setActiveModal,
        handleSelectedMetricTypes,
        selectedMetricTypes,
        setSelectedMetricTypes,
        filterMetricsByGroup,
        usageLimits,
        setFilteredMetrics,
        filteredMetrics,
        renderIntervalSettings,
        isIntervalLimitOverExceeded,
        setInteractiveIntervalUsage,
        handleIncidentMenu,
        rulesetFormInitialValues,
        fetchRulesets
    }
}

export default useHeaderActions
